import React from 'react';
import { Alert, Box, Button, Grid, Stack, TextField, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PublishIcon from '@mui/icons-material/Publish';
import { FWizard, FWizardChecklist, FWizardSummaryCard, FWizardStepConfig } from '../wizard';
import BaselineCoverageGrid from './BaselineCoverageGrid';
import BaselineIntegrityCards from './BaselineIntegrityCards';
import ForecastQualityPanel from './ForecastQualityPanel';
import ForecastVariableGrid from './ForecastVariableGrid';
import { createForecastBatch, fetchBaselineCoverage, fetchForecastQuality, fetchForecastRows, fetchForecastVariables, publishForecastBaseline, runMissingPeriodForecast } from './forecastBaseline.apis';

type State = {
  activeStep: string; fiscalyear: string; scenariocode: string; batchname: string; notes: string;
  loading: boolean; error: string; success: string; coverage: any[]; variables: any[]; batch: any | null; forecastRows: any[]; quality: any;
};

class ForecastBaselineWizard extends React.Component<any, State> {
  state: State = { activeStep: 'setup', fiscalyear: '2027', scenariocode: 'BASE2027', batchname: 'Forecast Baseline 2027', notes: '', loading: false, error: '', success: '', coverage: [], variables: [], batch: null, forecastRows: [], quality: {} };

  componentDidMount() { this.loadIntegrity(); }

  readiness = () => {
    const rows = this.state.coverage || [];
    return rows.length ? Math.round(rows.reduce((a, b) => a + Number(b.readiness || 0), 0) / rows.length) : 0;
  };

  blocked = () => (this.state.coverage || []).some((x) => x.status === 'BLOCKED');

  loadIntegrity = async () => {
    this.setState({ loading: true, error: '', success: '' });
    try {
      const [coverage, variables] = await Promise.all([fetchBaselineCoverage(this.state.fiscalyear, this.state.scenariocode), fetchForecastVariables(this.state.fiscalyear)]);
      this.setState({ coverage, variables });
    } catch (err: any) { this.setState({ error: err.message || String(err) }); }
    finally { this.setState({ loading: false }); }
  };

  createBatch = async () => {
    this.setState({ loading: true, error: '', success: '' });
    try {
      const batch = await createForecastBatch(this.state.fiscalyear, this.state.scenariocode, this.state.batchname);
      this.setState({ batch, success: 'Forecast batch created.' });
      return batch;
    } catch (err: any) { this.setState({ error: err.message || String(err) }); return null; }
    finally { this.setState({ loading: false }); }
  };

  runForecast = async () => {
    let batch = this.state.batch;
    if (!batch?.rid) batch = await this.createBatch();
    if (!batch?.rid) return;

    this.setState({ loading: true, error: '', success: '' });
    try {
      await runMissingPeriodForecast(batch.rid, {});
      const [forecastRows, quality] = await Promise.all([fetchForecastRows(batch.rid), fetchForecastQuality(batch.rid)]);
      this.setState({ forecastRows, quality, success: 'Missing periods forecast generated.', activeStep: 'quality' });
    } catch (err: any) { this.setState({ error: err.message || String(err) }); }
    finally { this.setState({ loading: false }); }
  };

  publish = async () => {
    if (!this.state.batch?.rid) return;
    this.setState({ loading: true, error: '', success: '' });
    try {
      await publishForecastBaseline(this.state.batch.rid, this.state.notes);
      this.setState({ success: 'Forecast baseline published.' });
    } catch (err: any) { this.setState({ error: err.message || String(err) }); }
    finally { this.setState({ loading: false }); }
  };

  renderSetup = () => (
    <Stack spacing={2}>
      <Alert severity="info">Forecasting consumes approved immutable facts. If facts are missing or not approved, resolve them in the Historical Data workbench before continuing.</Alert>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}><TextField size="small" label="Fiscal Year" value={this.state.fiscalyear} onChange={(e) => this.setState({ fiscalyear: e.target.value })} fullWidth /></Grid>
        <Grid size={{ xs: 12, md: 3 }}><TextField size="small" label="Scenario Code" value={this.state.scenariocode} onChange={(e) => this.setState({ scenariocode: e.target.value.toUpperCase() })} fullWidth /></Grid>
        <Grid size={{ xs: 12, md: 4 }}><TextField size="small" label="Batch Name" value={this.state.batchname} onChange={(e) => this.setState({ batchname: e.target.value })} fullWidth /></Grid>
        <Grid size={{ xs: 12, md: 2 }}><Button variant="contained" onClick={this.loadIntegrity} fullWidth>Check</Button></Grid>
      </Grid>
    </Stack>
  );

  renderIntegrity = () => (
    <Stack spacing={2}>
      <BaselineIntegrityCards rows={this.state.coverage} />
      <FWizardChecklist title="Integrity Gate" subtitle="Forecasting can continue only when historical fact gaps are resolved or explicitly accepted." items={[
        { code: 'facts', label: 'Approved facts are available', checked: this.state.coverage.length > 0, severity: 'warning' },
        { code: 'continuity', label: 'Historical fact coverage is continuous', checked: !this.blocked(), severity: 'error' },
        { code: 'variables', label: 'Forecast variables are configured', checked: this.state.variables.length > 0, severity: 'warning' },
      ]} />
      <BaselineCoverageGrid rows={this.state.coverage} loading={this.state.loading} />
    </Stack>
  );

  renderVariables = () => <ForecastVariableGrid rows={this.state.variables} loading={this.state.loading} />;

  renderForecast = () => (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}><FWizardSummaryCard label="Batch" value={this.state.batch?.batchcode || 'Not created'} tone={this.state.batch ? 'success' : 'warning'} /></Grid>
        <Grid size={{ xs: 12, md: 3 }}><FWizardSummaryCard label="Forecast Start" value={this.state.coverage.find((x) => x.forecaststart)?.forecaststart || '-'} tone="info" /></Grid>
        <Grid size={{ xs: 12, md: 3 }}><FWizardSummaryCard label="Readiness" value={`${this.readiness()}%`} tone={this.readiness() >= 90 ? 'success' : 'warning'} /></Grid>
      </Grid>
      <Alert severity="info">This step forecasts only the missing periods for variables earmarked as forecastable. Fixed assumptions remain in the Assumptions Panel.</Alert>
      <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={this.runForecast} disabled={this.blocked() || this.state.loading}>Generate Forecast for Missing Periods</Button>
    </Stack>
  );

  renderQuality = () => <ForecastQualityPanel quality={this.state.quality} />;

  renderPublish = () => (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">Publish the completed forecast baseline so it becomes available to Interactive Simulation.</Typography>
      <TextField label="Publication Notes" value={this.state.notes} onChange={(e) => this.setState({ notes: e.target.value })} multiline minRows={3} fullWidth />
      <Button variant="contained" color="success" startIcon={<PublishIcon />} onClick={this.publish} disabled={!this.state.batch?.rid || this.state.loading}>Publish Forecast Baseline</Button>
    </Stack>
  );

  steps = (): FWizardStepConfig[] => [
    { code: 'setup', title: 'Forecast Setup', subtitle: 'Choose fiscal year, scenario and batch.', description: 'Define the forecasting exercise.', status: this.state.activeStep === 'setup' ? 'active' : 'complete', canContinue: true, render: this.renderSetup },
    { code: 'integrity', title: 'Baseline Integrity Check', subtitle: 'Review coverage, continuity and missing periods.', description: 'Checks fact continuity and identifies forecast start.', status: this.blocked() ? 'warning' : 'complete', canContinue: !this.blocked(), requirement: this.blocked() ? 'Resolve missing approved facts in Historical Data.' : undefined, render: this.renderIntegrity },
    { code: 'variables', title: 'Forecast Variables', subtitle: 'Review configurable variables and sources.', description: 'Only forecastable variables are estimated.', status: this.state.variables.length ? 'complete' : 'warning', canContinue: this.state.variables.length > 0, render: this.renderVariables },
    { code: 'forecast', title: 'Forecast Missing Periods', subtitle: 'Generate estimates for unknown months.', description: 'Forecasts missing periods only.', status: this.state.forecastRows.length ? 'complete' : 'pending', canEnter: !this.blocked(), canContinue: true, render: this.renderForecast },
    { code: 'quality', title: 'Forecast Quality Review', subtitle: 'Review confidence and exceptions.', description: 'Explains forecast output before publication.', status: this.state.quality?.status === 'READY' ? 'complete' : 'warning', canContinue: true, render: this.renderQuality },
    { code: 'publish', title: 'Publish Forecast Baseline', subtitle: 'Make baseline available to simulation.', description: 'Published baselines become controlled simulation inputs.', status: this.state.batch?.statecode === 'PUBLISHED' ? 'complete' : 'pending', canContinue: Boolean(this.state.batch?.rid), render: this.renderPublish },
  ];

  render() {
    return (
      <Box sx={{ p: 3 }}>
        <FWizard title="Forecast & Baseline Wizard" subtitle="Assess approved facts, forecast missing periods, and publish the completed baseline for Interactive Simulation." activeStep={this.state.activeStep} steps={this.steps()} loading={this.state.loading} error={this.state.error} success={this.state.success} onStepChange={(activeStep) => this.setState({ activeStep })} onFinish={this.publish} finishLabel="Publish" />
      </Box>
    );
  }
}

export default ForecastBaselineWizard;
